/* eslint-disable react-hooks/set-state-in-effect, react-hooks/set-state-in-render --
 * v77.99.78: CalendarExportDialog seeds default date range from
 * monthOffset on open. Intentional and audited.
 */
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, getToken } from "@/lib/api";

/**
 * v77.99.78 — One-click ICS export for the calendar.
 *
 * Lets the user pick:
 *   - Date range (defaults to the visible month + 3 months ahead so
 *     freshly-imported calendars feel populated, not empty)
 *   - Child filter (one specific child or "All my children")
 *   - Whether to include custody handovers as 30-min timed events
 *
 * Calls `GET /api/calendar/export.ics` with `Authorization: Bearer`
 * (NOT a cookie) so the browser's fetch + blob save path doesn't need
 * to deal with CSRF. The endpoint returns `text/calendar` which every
 * major calendar app accepts as an import.
 */
export function CalendarExportDialog({ open, onOpenChange, childList, monthOffset = 0 }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [scopeChildId, setScopeChildId] = useState("__all__");
  const [includeHandovers, setIncludeHandovers] = useState(true);
  const [busy, setBusy] = useState(false);

  // Seed defaults when the dialog opens. We pick a range anchored on
  // the visible month: 1st of (this month + monthOffset) → end of
  // (anchor + 3 months). This matches the user's mental model — they
  // see Apr while looking at the calendar, the dialog suggests
  // "Apr → Jul", and the imported calendar lands feeling alive.
  useEffect(() => {
    if (!open) return;
    const today = new Date();
    const anchor = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
    const tail = new Date(anchor.getFullYear(), anchor.getMonth() + 4, 0);
    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    setStart(fmt(anchor));
    setEnd(fmt(tail));
    setScopeChildId("__all__");
    setIncludeHandovers(true);
  }, [open, monthOffset]);

  const valid = useMemo(() => {
    if (!start || !end) return false;
    if (end < start) return false;
    // Server enforces 366-day cap; mirror it client-side so the user
    // gets immediate feedback rather than waiting on the request.
    const s = new Date(start);
    const e = new Date(end);
    const days = Math.round((e - s) / (1000 * 60 * 60 * 24));
    return days <= 366;
  }, [start, end]);

  async function handleExport() {
    if (!valid) {
      toast.error("Pick a valid date range — up to 1 year.");
      return;
    }
    setBusy(true);
    try {
      const token = getToken();
      // v77.99.102 — getToken() is now a no-op returning ""; cookie
      // covers authentication. Block only on hard failures below.
      const params = new URLSearchParams({ start, end });
      if (scopeChildId && scopeChildId !== "__all__") params.set("child_id", scopeChildId);
      if (includeHandovers) params.set("include_handovers", "true");
      const resp = await fetch(`${API}/calendar/export.ics?${params.toString()}`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || `Export failed (${resp.status})`);
      }
      const blob = await resp.blob();
      // Count VEVENTs so the toast can say "47 events exported" — much
      // more reassuring than a generic "Downloaded".
      const text = await blob.text();
      const eventCount = (text.match(/BEGIN:VEVENT/g) || []).length;
      const url = URL.createObjectURL(new Blob([text], { type: "text/calendar" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `coparent-calendar-${start}-to-${end}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Calendar exported — ${eventCount} event${eventCount === 1 ? "" : "s"}.`, {
        description: "Open the file in Google Calendar, Apple Calendar, or Outlook to import.",
      });
      onOpenChange?.(false);
    } catch (err) {
      toast.error("Couldn't export calendar.", {
        description: err?.message || "Please try a smaller date range.",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid="calendar-export-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#2E3A3F] flex items-center gap-2">
            <Download className="w-5 h-5 text-[#1F9FA4]" />
            Sync to Google / Apple Calendar
          </DialogTitle>
          <DialogDescription className="text-sm text-[#6F828A]">
            Download an .ics snapshot. Open it on any phone or laptop to import into your default calendar app.
            Re-export whenever you want fresh data — there&apos;s no live sync.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="export-start" className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">From</Label>
              <Input
                id="export-start"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                data-testid="calendar-export-start"
              />
            </div>
            <div>
              <Label htmlFor="export-end" className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">To</Label>
              <Input
                id="export-end"
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                data-testid="calendar-export-end"
              />
            </div>
          </div>

          {(childList || []).length > 0 && (
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">Children</Label>
              <Select value={scopeChildId} onValueChange={setScopeChildId}>
                <SelectTrigger data-testid="calendar-export-child-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All my children</SelectItem>
                  {childList.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`calendar-export-child-${c.id}`}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center justify-between rounded-md border border-[#D9E1E3] bg-[#F7FAFA] px-3 py-2.5">
            <div className="pr-3">
              <div className="text-sm font-medium text-[#2E3A3F]">Include custody handovers</div>
              <div className="text-[11px] text-[#6F828A] leading-snug mt-0.5">
                30-minute timed events at each handover moment (e.g. &ldquo;Handover: Alex → Sam — Mia&rdquo;).
              </div>
            </div>
            <Switch
              checked={includeHandovers}
              onCheckedChange={setIncludeHandovers}
              data-testid="calendar-export-handovers-toggle"
            />
          </div>

          {!valid && start && end && (
            <p className="text-xs text-[#C0291D]" data-testid="calendar-export-range-error">
              {end < start
                ? "End date must be on or after start date."
                : "Range too wide — pick at most 1 year."}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            data-testid="calendar-export-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={!valid || busy}
            className="bg-[#1F9FA4] hover:bg-[#116B6E] text-white"
            data-testid="calendar-export-confirm-btn"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Building file…
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Download .ics
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
